import { ProgressBar, Text, makeStyles, tokens } from '@fluentui/react-components';
import type { ProgressBarProps } from '@fluentui/react-components';
import { formatUsage } from '@worklogix/shared';
import type { TenantUsage } from '@worklogix/shared';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    minWidth: '120px',
  },
  label: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalS,
  },
  overLimit: {
    color: tokens.colorStatusWarningForeground1,
  },
});

export interface UsageMeterProps {
  /** The tenant's user-count-vs-cap usage snapshot. */
  usage: TenantUsage;
}

/**
 * Renders a tenant's seat usage as `used / cap` (∞ for an unlimited cap, via the
 * shared {@link formatUsage}) above a Fluent {@link ProgressBar}.
 *
 * Soft-warn only: when {@link TenantUsage.overLimit} is set the bar switches to
 * the amber `warning` color and the count is tinted with the warning token —
 * never the `error` color and never a disabled/blocked treatment. The over-limit
 * rule itself is owned by the shared compliance module, not recomputed here.
 */
export function UsageMeter({ usage }: UsageMeterProps): JSX.Element {
  const styles = useStyles();

  const { used, cap, overLimit } = usage;
  // Unlimited (cap === null) has no meaningful fraction of an infinite cap → 0.
  // A finite cap fills proportionally, clamped to a full bar when at/over limit.
  const value = cap === null ? 0 : cap <= 0 ? 1 : Math.min(used / cap, 1);
  const color: NonNullable<ProgressBarProps['color']> = overLimit ? 'warning' : 'brand';

  return (
    <div className={styles.root}>
      <div className={styles.label}>
        <Text size={200} className={overLimit ? styles.overLimit : undefined}>
          {formatUsage(usage)}
        </Text>
        {overLimit && (
          <Text size={200} weight="semibold" className={styles.overLimit}>
            over limit
          </Text>
        )}
      </div>
      <ProgressBar
        value={value}
        max={1}
        color={color}
        thickness="medium"
        shape="rounded"
        aria-label={`Seat usage: ${formatUsage(usage)}`}
      />
    </div>
  );
}
